/*
 * Состояние самого интерфейса: ширина панели, открытое контекстное меню,
 * просмотр изображений.
 *
 * Отдельно от `app.svelte.ts` намеренно: там серверные доменные данные, здесь —
 * ширина панелей и временные окна, которые остаются только в браузере.
 */

import type { Attachment } from '@andresot/protocol';
import type { MenuItem, MenuReaction } from '$components/ContextMenu.svelte';
import { app } from './app.svelte';

const WIDTH_KEY = 'andresot:panel-width';

/** Разделы настроек — по порядку списка в образце. */
export type SettingsSection =
	| 'profile'
	| 'premium'
	| 'folders'
	| 'security'
	| 'devices'
	| 'notifications'
	| 'appearance'
	| 'media'
	| 'language'
	| 'shortcuts'
	| 'mail'
	| 'admin'
	| 'help'
	| 'about';

export type AdminSection =
	| 'dashboard'
	| 'users'
	| 'moderation'
	| 'channels'
	| 'calls'
	| 'payments'
	| 'gifts'
	| 'vpn'
	| 'broadcasts'
	| 'learning'
	| 'support'
	| 'storage'
	| 'security'
	| 'system'
	| 'roles'
	| 'settings';

export type AdminRecordKind =
	| 'user'
	| 'report'
	| 'channel'
	| 'call'
	| 'payment'
	| 'broadcast'
	| 'ticket'
	| 'security'
	| 'audit';

export interface AdminRecordRef {
	kind: AdminRecordKind;
	id: string;
}

export type ServiceName =
	| 'trading'
	| 'finance'
	| 'workouts'
	| 'learning'
	| 'vpn'
	| 'video'
	| 'news'
	| 'dating'
	| 'music'
	| 'classifieds';

/** Границы совпадают с --panel-secondary-min/max в packages/tokens. */
const MIN_WIDTH = 260;
const MAX_WIDTH = 592;
const DEFAULT_WIDTH = 393;

interface ContextMenuState {
	items: MenuItem[];
	/** Ряд реакций над списком — только у меню сообщения. */
	reactions?: MenuReaction[];
	x: number;
	y: number;
}

/**
 * Снимок в просмотрщике вместе с тем, откуда он взят.
 *
 * Раньше просмотрщик получал голые вложения, и «перейти к сообщению» ему было
 * не из чего собрать. Источник знает не всякий список: у собранных не из
 * переписки его просто нет, и эти два действия там не показываются.
 */
export interface LightboxItem {
	attachment: Attachment;
	messageId?: string;
	chatId?: string;
}

interface LightboxState {
	items: LightboxItem[];
	index: number;
	caption?: string;
}

class UiState {
	readonly minWidth = MIN_WIDTH;
	readonly maxWidth = MAX_WIDTH;

	panelWidth = $state(loadWidth());
	contextMenu = $state<ContextMenuState | null>(null);
	lightbox = $state<LightboxState | null>(null);
	/** Открытое исчезающее вложение. Живёт отдельно от обычного просмотра. */
	ephemeral = $state<{ chatId: string; messageId: string } | null>(null);

	/**
	 * Узкий экран: три панели рядом не помещаются, раскладка становится
	 * поочерёдной. Порог совпадает с --breakpoint-mobile в токенах.
	 */
	isMobile = $state(false);

	/**
	 * Какая панель показана на узком экране. На широком не используется —
	 * там обе видны одновременно.
	 */
	mobilePane = $state<'list' | 'content'>('list');

	/** Следить за шириной окна. Вызывается один раз при запуске. */
	watchViewport(): () => void {
		if (typeof window === 'undefined') return () => {};

		const query = window.matchMedia('(max-width: 900px)');

		const sync = () => {
			this.isMobile = query.matches;
			// Возврат на широкий экран сбрасывает поочерёдность: иначе список
			// остался бы «спрятанным», хотя места рядом уже хватает.
			if (!query.matches) this.mobilePane = 'list';
			else if (app.section === 'settings' && this.settingsSection === 'admin') {
				this.mobilePane = 'content';
			}
		};

		sync();
		query.addEventListener('change', sync);

		return () => query.removeEventListener('change', sync);
	}

	/** Показать содержимое поверх списка — только на узком экране. */
	showContent(): void {
		if (this.isMobile) this.mobilePane = 'content';
	}

	/**
	 * Видна ли человеку лента этого чата — для отметки «прочитано».
	 *
	 * В «Настройках», «Почте» и «Сервисах» место переписки занято другим. На телефоне
	 * лента видна, только пока открыт экран чата: под списком и под сведениями о чате
	 * её не читают. На компьютере сведения открываются рядом, лента остаётся на месте.
	 */
	chatFeedVisible(chatId: string): boolean {
		if (app.selectedChatId !== chatId) return false;
		if (app.section === 'settings' || app.section === 'mail' || app.section === 'services') {
			return false;
		}
		return !this.isMobile || (this.mobilePane === 'content' && !app.infoPanelOpen);
	}

	/*
	 * Открытие чата и раздела настроек проходит через эти два метода,
	 * а не через присваивание на местах. Причина простая: на узком экране
	 * мало выбрать — нужно ещё показать правую панель, и когда это делалось
	 * в каждой кнопке отдельно, половина кнопок про переключение забывала.
	 */

	/**
	 * Открыть чат: выбрать и, на телефоне, перейти на его экран.
	 *
	 * Сначала — раздел, в котором переписка вообще рисуется. В «Сервисах»,
	 * «Настройках» и «Почте» её место занято другим, и выбранный чат там просто
	 * оставался невидимым: нажатие на уведомление, возврат из звонка и ссылка
	 * «Поддержка» открывали «ничего».
	 */
	openChat(chatId: string): void {
		if (app.section !== 'chats' && app.section !== 'calls' && app.section !== 'contacts') {
			app.setSection('chats');
		}
		app.selectChat(chatId);
		this.showContent();
	}

	/** Открыть раздел настроек. */
	openSettingsSection(section: SettingsSection): void {
		this.settingsSection = section;
		this.showContent();
	}

	/** Вернуться к списку. */
	showList(): void {
		this.mobilePane = 'list';
		app.infoPanelOpen = false;
	}

	/**
	 * Канал, из которого владелец собрался выйти.
	 *
	 * Просто выйти он не может — канал остался бы без хозяина. Поэтому меню
	 * не отказывает, а открывает сведения канала: там владельца встречает
	 * передача прав, а если передавать некому — удаление.
	 */
	channelExitFor = $state<string | null>(null);

	openChannelExit(chatId: string): void {
		this.channelExitFor = chatId;
		app.selectChat(chatId);
		app.infoPanelOpen = true;
		this.showContent();
		this.closeContextMenu();
	}

	/** Какой раздел настроек показан в правой панели. */
	settingsSection = $state<SettingsSection>('profile');
	/** Мобильная админка хранит навигацию отдельно от серверных данных. */
	adminSection = $state<AdminSection>('dashboard');
	adminDetail = $state<AdminRecordRef | null>(null);
	adminQuery = $state('');
	adminHistoryPosition = 0;
	/** Вкладки административных разделов живут только в текущей сессии. */
	adminTabs = $state<Partial<Record<AdminSection, string>>>({});

	setAdminTab(section: AdminSection, tab: string): void {
		this.adminTabs = { ...this.adminTabs, [section]: tab };
	}
	adminFilters = $state<Partial<Record<AdminSection, Record<string, string>>>>({});
	adminScroll = $state<Partial<Record<AdminSection, number>>>({});
	/** Открыт редактор папки; `'new'` — создание новой. */
	folderEditorId = $state<string | null>(null);
	/**
	 * Чат, отмеченный в новой папке заранее. Папку создают из меню самого
	 * чата — и уйти оттуда с пустой папкой значило бы проделать полпути.
	 */
	folderEditorChatIds = $state<string[]>([]);
	/** Открыто окно приглашения по ссылке. */
	inviteOpen = $state(false);
	contactSearchRequested = $state(false);

	openContactSearch(): void {
		this.contactSearchRequested = true;
		app.setSection('contacts');
		this.showList();
	}
	/** Чей контакт настраиваем — своё имя, фамилия и удаление из книжки. */
	contactSettingsUserId = $state<string | null>(null);
	/** Чью визитку отправляем и из какого чата её открыли. */
	shareContact = $state<{ userId: string; fromChatId: string } | null>(null);
	/**
	 * Открыт ли экран Premium поверх приложения и по чьему знаку.
	 *
	 * `null` — закрыт, `''` — открыт про себя (из настроек или по отказу
	 * из-за предела), идентификатор — открыт по чужому знаку, и тогда сверху
	 * стоит строка о том, чья это подписка. Пустая строка вместо второго поля:
	 * состояний ровно три, и разделять их двумя переменными значило бы
	 * допустить четвёртое, невозможное.
	 */
	premiumScreenFor = $state<string | null>(null);
	/** Получатель полноэкранного каталога подарков. */
	giftRecipientId = $state<string | null>(null);

	openGiftCatalog(recipientId: string): void {
		this.giftRecipientId = recipientId;
		this.closeContextMenu();
	}
	/** Открытый рабочий сервис; нужен также для глубокой ссылки из push. */
	activeService = $state<ServiceName | null>(null);
	/** Для какого чата создаётся настоящая серверная ссылка-приглашение. */
	chatInviteId = $state<string | null>(null);
	/** В какой чат выбираются существующие контакты. */
	addMembersChatId = $state<string | null>(null);
	/** В каком чате открыт полнотекстовый поиск. */
	chatSearchId = $state<string | null>(null);
	/** В каком чате создаётся опрос. */
	pollChatId = $state<string | null>(null);
	/** В каком чате выбирают, чью визитку отправить. */
	contactPickerChatId = $state<string | null>(null);
	/** Открытая ветка комментариев канала. */
	channelCommentTarget = $state<{ chatId: string; postId: string } | null>(null);
	/** Открыто окно создания группы, канала или группового звонка. */
	createChatKind = $state<'group' | 'channel' | 'call' | null>(null);
	/** Какие сообщения сейчас пересылаются; пустой список закрывает диалог. */
	forwardMessageIds = $state<string[]>([]);

	openCreateChat(kind: 'group' | 'channel' | 'call'): void {
		this.createChatKind = kind;
		this.closeContextMenu();
	}

	closeCreateChat(): void {
		this.createChatKind = null;
	}

	openChatInvite(chatId: string): void {
		this.chatInviteId = chatId;
		this.closeContextMenu();
	}

	closeChatInvite(): void {
		this.chatInviteId = null;
	}

	openChannelComments(chatId: string, postId: string): void {
		this.channelCommentTarget = { chatId, postId };
		this.closeContextMenu();
	}

	closeChannelComments(): void {
		this.channelCommentTarget = null;
	}

	openAddMembers(chatId: string): void {
		this.addMembersChatId = chatId;
		this.closeContextMenu();
	}

	closeAddMembers(): void {
		this.addMembersChatId = null;
	}

	openChatSearch(chatId: string): void {
		this.chatSearchId = chatId;
		this.closeContextMenu();
	}

	closeChatSearch(): void {
		this.chatSearchId = null;
	}

	openPoll(chatId: string): void {
		this.pollChatId = chatId;
		this.closeContextMenu();
	}

	closePoll(): void {
		this.pollChatId = null;
	}

	openContactPicker(chatId: string): void {
		this.contactPickerChatId = chatId;
		this.closeContextMenu();
	}

	closeContactPicker(): void {
		this.contactPickerChatId = null;
	}

	openForward(messageIds: string[]): void {
		this.forwardMessageIds = [...new Set(messageIds)];
		this.closeContextMenu();
	}

	closeForward(): void {
		this.forwardMessageIds = [];
	}

	/**
	 * Показать экран Premium поверх приложения.
	 *
	 * Без аргумента — про себя, с идентификатором — про владельца знака.
	 * Отдельно от раздела настроек: знак стоит рядом с именем в переписке,
	 * и уводить оттуда в настройки значило бы потерять чат.
	 */
	openPremium(userId?: string): void {
		this.premiumScreenFor = userId ?? '';
		this.closeContextMenu();
	}

	closePremium(): void {
		this.premiumScreenFor = null;
	}

	openService(service: ServiceName): void {
		app.setSection('services');
		this.activeService = service;
		this.showContent();
		this.closeContextMenu();
	}

	closeService(): void {
		this.activeService = null;
		/*
		 * И панель обратно на список. Без этого закрытый сервис оставлял за
		 * собой «показана правая панель», а на разделе сервисов правой панели
		 * нет вовсе — каталог оставался без нижнего меню, и уйти с него было
		 * нечем.
		 */
		this.showList();
	}

	/** Перейти в настройки и сразу открыть нужный раздел. */
	openSettings(section: SettingsSection): void {
		app.setSection('settings');
		this.settingsSection = section;
		this.showContent();
		this.closeContextMenu();
	}

	openAdminSettings(): void {
		app.setSection('settings');
		this.settingsSection = 'admin';
		this.adminDetail = null;
		this.showContent();
		this.closeContextMenu();
		this.writeAdminUrl('push');
	}

	openAdminSection(section: AdminSection): void {
		this.adminSection = section;
		this.adminDetail = null;
		this.writeAdminUrl('replace');
	}

	openAdminDetail(detail: AdminRecordRef): void {
		this.adminDetail = detail;
		this.writeAdminUrl('push');
	}

	setAdminScroll(section: AdminSection, top: number): void {
		this.adminScroll = { ...this.adminScroll, [section]: Math.max(0, top) };
	}

	setAdminFilters(section: AdminSection, filters: Record<string, string>): void {
		this.adminFilters = { ...this.adminFilters, [section]: { ...filters } };
	}

	restoreAdminFromUrl(): boolean {
		if (typeof window === 'undefined') return false;
		this.adminHistoryPosition = window.history.state?.andresotAdminPosition ?? 0;
		const url = new URL(window.location.href);
		if (
			url.searchParams.get('section') !== 'settings' ||
			url.searchParams.get('settings') !== 'admin'
		) {
			return false;
		}

		const section = url.searchParams.get('admin');
		if (isAdminSection(section)) this.adminSection = section;
		this.adminDetail = parseAdminItem(url.searchParams.get('adminItem'));
		app.setSection('settings');
		this.settingsSection = 'admin';
		this.showContent();
		return true;
	}

	handleAdminPopState(): void {
		if (this.restoreAdminFromUrl()) return;
		this.adminDetail = null;
		if (this.settingsSection === 'admin') this.showList();
	}

	private writeAdminUrl(mode: 'push' | 'replace'): void {
		if (typeof window === 'undefined') return;
		if (mode === 'push') this.adminHistoryPosition += 1;
		const url = new URL(window.location.href);
		url.searchParams.set('section', 'settings');
		url.searchParams.set('settings', 'admin');
		url.searchParams.set('admin', this.adminSection);
		if (this.adminDetail) {
			url.searchParams.set('adminItem', `${this.adminDetail.kind}:${this.adminDetail.id}`);
		} else {
			url.searchParams.delete('adminItem');
		}
		const next = `${url.pathname}${url.search}${url.hash}`;
		window.history[mode === 'push' ? 'pushState' : 'replaceState'](
			{
				...window.history.state,
				andresotAdmin: true,
				andresotAdminPosition: this.adminHistoryPosition
			},
			'',
			next
		);
	}

	openFolderEditor(folderId: string | 'new', chatIds: string[] = []): void {
		this.folderEditorId = folderId;
		this.folderEditorChatIds = chatIds;
		this.closeContextMenu();
	}

	closeFolderEditor(): void {
		this.folderEditorId = null;
		this.folderEditorChatIds = [];
	}

	setPanelWidth(width: number): void {
		this.panelWidth = Math.min(Math.max(width, MIN_WIDTH), MAX_WIDTH);

		try {
			localStorage.setItem(WIDTH_KEY, String(this.panelWidth));
		} catch {
			// Приватный режим — ширина просто не переживёт перезагрузку.
		}
	}

	openContextMenu(event: MouseEvent, items: MenuItem[], reactions?: MenuReaction[]): void {
		event.preventDefault();
		this.contextMenu = { items, reactions, x: event.clientX, y: event.clientY };
	}

	closeContextMenu(): void {
		this.contextMenu = null;
	}

	openLightbox(items: LightboxItem[], index = 0, caption?: string): void {
		// Просмотр открывается только для изображений и видео: файлы и голосовые
		// в нём показать нечем.
		const viewable = items.filter(
			(item) => item.attachment.kind === 'image' || item.attachment.kind === 'video'
		);
		if (viewable.length === 0) return;

		this.lightbox = {
			items: viewable,
			index: Math.max(0, Math.min(index, viewable.length - 1)),
			caption
		};
	}

	closeLightbox(): void {
		this.lightbox = null;
	}

	openEphemeral(chatId: string, messageId: string): void {
		this.ephemeral = { chatId, messageId };
	}

	closeEphemeral(): void {
		this.ephemeral = null;
	}
}

function loadWidth(): number {
	try {
		const raw = localStorage.getItem(WIDTH_KEY);
		const parsed = raw ? Number(raw) : NaN;

		return Number.isFinite(parsed)
			? Math.min(Math.max(parsed, MIN_WIDTH), MAX_WIDTH)
			: DEFAULT_WIDTH;
	} catch {
		return DEFAULT_WIDTH;
	}
}

const ADMIN_SECTIONS = new Set<AdminSection>([
	'dashboard',
	'users',
	'moderation',
	'channels',
	'calls',
	'payments',
	'gifts',
	'vpn',
	'learning',
	'broadcasts',
	'support',
	'storage',
	'security',
	'system',
	'roles',
	'settings'
]);

const ADMIN_RECORD_KINDS = new Set<AdminRecordKind>([
	'user',
	'report',
	'channel',
	'call',
	'payment',
	'broadcast',
	'ticket',
	'security',
	'audit'
]);

function isAdminSection(value: string | null): value is AdminSection {
	return value !== null && ADMIN_SECTIONS.has(value as AdminSection);
}

function parseAdminItem(value: string | null): AdminRecordRef | null {
	if (!value) return null;
	const separator = value.indexOf(':');
	if (separator < 1) return null;
	const kind = value.slice(0, separator) as AdminRecordKind;
	const id = value.slice(separator + 1);
	return ADMIN_RECORD_KINDS.has(kind) && id ? { kind, id } : null;
}

export const ui = new UiState();

// Хранилище переписки не знает, какая панель на экране: спрашивает у оболочки.
app.feedVisible = (chatId) => ui.chatFeedVisible(chatId);
